import { useState, useEffect } from 'react';
import { config } from '../config';

// Module-level cache so the provider is fetched once per page load and shared
// across every hook instance.
let cachedData = null;
let fetchPromise = null;

/**
 * Resolve the active payment provider (Stripe or Paddle) from the backend.
 *
 * The backend is the source of truth — `config.PAYMENT_PROVIDER` is only a
 * first-paint default. Returns the Paddle client token + environment when
 * Paddle is active, so the checkout can initialize Paddle.js safely (prod never
 * points at sandbox).
 */
export const usePaymentProvider = () => {
  const initial = cachedData || { provider: config.PAYMENT_PROVIDER || 'paddle' };
  const [provider, setProvider] = useState(initial.provider);
  const [paddleClientToken, setPaddleClientToken] = useState(initial.paddleClientToken || null);
  const [paddleEnvironment, setPaddleEnvironment] = useState(initial.paddleEnvironment || 'sandbox');
  const [loading, setLoading] = useState(!cachedData);

  useEffect(() => {
    if (cachedData) {
      setProvider(cachedData.provider);
      setPaddleClientToken(cachedData.paddleClientToken || null);
      setPaddleEnvironment(cachedData.paddleEnvironment || 'sandbox');
      setLoading(false);
      return;
    }

    if (!fetchPromise) {
      fetchPromise = fetch(`${config.API_URI}/v1/paddle/config`, { credentials: 'include' })
        .then(res => res.json())
        .then(data => {
          cachedData = {
            provider: data.provider || config.PAYMENT_PROVIDER || 'paddle',
            paddleClientToken: data.paddleClientToken || null,
            paddleEnvironment: data.paddleEnvironment || 'sandbox'
          };
          return cachedData;
        })
        .catch(() => {
          // Config fetch failed — keep the app default (Paddle) rather than
          // falling back to a Stripe form that can't complete.
          cachedData = { provider: config.PAYMENT_PROVIDER || 'paddle' };
          return cachedData;
        });
    }

    let active = true;
    fetchPromise.then(data => {
      if (!active) return;
      setProvider(data.provider);
      setPaddleClientToken(data.paddleClientToken || null);
      setPaddleEnvironment(data.paddleEnvironment || 'sandbox');
      setLoading(false);
    });
    return () => { active = false; };
  }, []);

  return {
    provider,
    isStripe: provider === 'stripe',
    isPaddle: provider === 'paddle',
    paddleClientToken,
    paddleEnvironment,
    loading,
    apiBase: provider === 'paddle' ? '/v1/paddle' : '/v1/stripe'
  };
};
